/**
 * UploadPanel — drag-drop MULTIPLE bank statements at once (PDF/CSV).
 *
 * Uploads run in parallel behind one HTTP request (FastAPI receives them
 * as a list). Backend parses each file, categorizes rows via Claude,
 * and persists. Result summary appears inline.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Upload, FileText, Loader2, Check, AlertCircle, Trash2, Building,
} from "lucide-react";
import { toast } from "sonner";
import {
  uploadStatements, listStatements, deleteStatement, getStatementStatus,
} from "@/lib/operations/api";
import ConfirmDeleteDialog from "@/pages/operations/ConfirmDeleteDialog";

// Fixed sponsor roster shown as a toggle on upload.
const DEFAULT_COMPANIES = ["420", "HepCURE", "Christian", "Joséphine", "George", "Racquel"];
// Special filter key — when active, the imported-statements list shows
// every company. Uploads while "All" is active fall back to the last
// specific company selected.
const ALL_COMPANIES = "__all__";

// Statement type toggle. Persisted so operators can filter later.
const STATEMENT_TYPES = [
  { key: "bank",        label: "Bank"        },
  { key: "credit_card", label: "Credit card" },
];

// Parse a possibly-fuzzy transaction date string into a Date. Falls back
// to null when it can't. Supports ISO-ish, "YYYY-MM-DD", "MM/DD/YYYY",
// "DD/MM/YYYY" and short "Mon DD" / "Mon DD YYYY".
const parseTxDate = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;
  // Try direct Date parsing first (handles ISO + many US formats).
  const direct = new Date(s);
  if (!Number.isNaN(direct.getTime())) return direct;
  // Try DD/MM/YYYY (Canadian-style).
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (m) {
    const dd = Number(m[1]), mm = Number(m[2]), yr = Number(m[3].length === 2 ? "20" + m[3] : m[3]);
    const dt = new Date(yr, mm - 1, dd);
    if (!Number.isNaN(dt.getTime())) return dt;
  }
  return null;
};

// Build a human-friendly range + month label for a statement.
// Returns { range: "Apr 3 – Apr 30, 2026", month: "April 2026" } or null.
const formatStatementPeriod = (s) => {
  const start = parseTxDate(s.period_start);
  const end = parseTxDate(s.period_end);
  if (!start && !end) return null;
  const a = start || end;
  const b = end || start;
  const sameYear = a.getFullYear() === b.getFullYear();
  const sameMonth = sameYear && a.getMonth() === b.getMonth();
  const optShort = { month: "short", day: "numeric" };
  const optLong = { month: "long", year: "numeric" };
  const startLabel = a.toLocaleDateString(undefined, optShort);
  const endLabel = b.toLocaleDateString(undefined, sameYear ? optShort : { ...optShort, year: "numeric" });
  const range = a.getTime() === b.getTime()
    ? `${startLabel}, ${a.getFullYear()}`
    : `${startLabel} – ${endLabel}${sameYear ? `, ${a.getFullYear()}` : ""}`;
  // "Statement month" — the period-end month is the canonical one for
  // bank/credit-card statements (they close at month end).
  const month = sameMonth
    ? a.toLocaleDateString(undefined, optLong)
    : b.toLocaleDateString(undefined, optLong);
  return { range, month };
};


export default function UploadPanel({ onImported, onOpenStatement, sponsor = "" }) {
  const [statements, setStatements] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lastResults, setLastResults] = useState([]);
  const [aiProgress, setAiProgress] = useState({}); // { statement_id: { done, total, status } }
  const [pendingDelete, setPendingDelete] = useState(null); // statement being deleted
  // Track the last SPECIFIC sponsor the operator picked so uploads
  // always land on a real sponsor even while "All" is active. Follows
  // the global sponsor filter.
  const [lastCompany, setLastCompany] = useState(sponsor || "420");
  useEffect(() => { if (sponsor) setLastCompany(sponsor); }, [sponsor]);
  // Currently-active sponsor for this panel. Mirrors the global filter.
  const company = sponsor || ALL_COMPANIES;
  // Whether this upload is a bank statement or a credit-card statement.
  const [statementType, setStatementType] = useState("bank");
  const inputRef = useRef(null);

  const reload = useCallback(async () => {
    try { setStatements(await listStatements()); }
    catch { /* handled elsewhere */ }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const doUpload = async (fileList) => {
    const files = [...fileList].filter((f) => /\.(pdf|csv)$/i.test(f.name));
    if (files.length === 0) {
      toast.error("Only PDF or CSV files are supported");
      return;
    }
    setUploading(true);
    setProgress(0);
    setLastResults([]);
    try {
      // When "All" is the current filter, fall back to the last
      // specific sponsor the operator picked so we always tag rows.
      const uploadCompany = (company === ALL_COMPANIES ? lastCompany : company).trim();
      const res = await uploadStatements(files, setProgress, uploadCompany, statementType);
      setLastResults(res.results || []);
      const okCount = (res.results || []).filter((r) => r.ok).length;
      const failCount = (res.results || []).length - okCount;
      if (okCount) toast.success(`${okCount} file${okCount === 1 ? "" : "s"} imported — AI is now classifying`);
      if (failCount) toast.error(`${failCount} file${failCount === 1 ? "" : "s"} failed`);
      await reload();
      onImported?.();
      // Start polling AI progress for each successful upload
      for (const r of res.results || []) {
        if (r.ok && r.statement_id && r.ai_status !== "done") {
          pollAi(r.statement_id, r.row_count);
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const pollAi = (id, total) => {
    let stopped = false;
    setAiProgress((p) => ({ ...p, [id]: { done: 0, total, status: "pending" } }));
    const tick = async () => {
      if (stopped) return;
      try {
        const s = await getStatementStatus(id);
        setAiProgress((p) => ({ ...p, [id]: { done: s.ai_done, total: s.row_count, status: s.ai_status } }));
        if (s.ai_status === "done" || s.ai_status === "failed" || s.ai_status === "disabled") {
          stopped = true;
          onImported?.();
          return;
        }
      } catch { /* keep polling on transient errors */ }
      setTimeout(tick, 3000);
    };
    setTimeout(tick, 1500);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) doUpload(e.dataTransfer.files);
  };

  const onDelete = (statement) => setPendingDelete(statement);

  const doDelete = async (pin) => {
    if (!pendingDelete) return;
    await deleteStatement(pendingDelete.id, pin);
    setStatements((cur) => cur.filter((s) => s.id !== pendingDelete.id));
    setPendingDelete(null);
    toast.success("Statement deleted");
    onImported?.();
  };

  return (
    <div className="space-y-6">
      {/* Statement type — pill-card style, kept above the drop zone so the
          operator declares the file's kind first. */}
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
          Statement type
        </p>
        <div className="mt-2 grid grid-cols-2 gap-2 max-w-xs">
          {STATEMENT_TYPES.map((s) => {
            const active = statementType === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setStatementType(s.key)}
                disabled={uploading}
                className={`inline-flex items-center justify-center h-10 px-2 rounded-xl border text-sm font-semibold transition min-w-0 ${
                  active
                    ? "bg-brand-navy text-white border-brand-navy shadow-sm"
                    : "bg-white text-brand-navy/80 border-slate-200 hover:border-brand-navy/40 hover:text-brand-navy"
                } disabled:opacity-50`}
                data-testid={`ops-upload-type-${s.key}`}
              >
                <span className="truncate">{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition ${
          dragOver
            ? "border-brand-teal bg-brand-teal/5"
            : "border-slate-300 bg-white hover:border-brand-teal/50 hover:bg-slate-50"
        } ${uploading ? "pointer-events-none opacity-70" : ""}`}
        data-testid="ops-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.csv,application/pdf,text/csv"
          multiple
          className="hidden"
          onChange={(e) => e.target.files && doUpload(e.target.files)}
        />
        <div className="h-14 w-14 rounded-full bg-brand-mist mx-auto flex items-center justify-center text-brand-navy">
          {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <Upload className="h-6 w-6" />}
        </div>
        <h3 className="mt-4 text-xl font-bold font-heading text-brand-navy">
          {uploading ? `Importing… ${progress}%` : "Drop one or more bank statements"}
        </h3>
        <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
          PDF or CSV — you can drop multiple files at once. We&apos;ll extract every
          transaction, propose a category, and flag anything unclear for your review.
        </p>
        <button
          type="button"
          disabled={uploading}
          className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white font-semibold shadow-sm transition disabled:opacity-50"
        >
          <Upload className="h-4 w-4" />
          Choose files
        </button>
        <p className="mt-2 text-xs text-slate-400">Max 25 MB per file</p>
      </div>

      {/* Result summary of the most recent batch */}
      {lastResults.length > 0 && (
        <div className="rounded-xl bg-slate-50 border border-slate-200 divide-y divide-slate-200 overflow-hidden">
          {lastResults.map((r, i) => {
            const ai = r.ok && r.statement_id ? aiProgress[r.statement_id] : null;
            const pct = ai && ai.total ? Math.round((ai.done / ai.total) * 100) : 0;
            return (
              <div key={i} className="px-4 py-3">
                <div className="flex items-center gap-3">
                  {r.ok
                    ? <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                    : <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-brand-navy truncate">{r.filename}</p>
                    {r.ok
                      ? <p className="text-xs text-slate-500">{r.row_count} transactions imported</p>
                      : <p className="text-xs text-red-600">{r.error}</p>}
                  </div>
                </div>
                {ai && ai.status !== "done" && ai.status !== "disabled" && (
                  <div className="mt-2 pl-7">
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                      <span className="flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" /> AI classifying {ai.done} of {ai.total}
                      </span>
                      <span className="tabular-nums">{pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
                      <div className="h-full bg-brand-teal transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )}
                {ai && ai.status === "done" && (
                  <p className="mt-1 pl-7 text-xs text-emerald-600 font-medium">AI classification complete</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Historical list */}
      <div>
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h3 className="text-lg font-bold text-brand-navy">Imported statements</h3>
          <span className="text-xs text-slate-500">
            {company === ALL_COMPANIES
              ? <>Showing <span className="font-semibold text-brand-navy">all sponsors</span></>
              : <>Showing <span className="font-semibold text-brand-navy">{company}</span> only</>}
          </span>
        </div>
        {(() => {
          const filtered = company === ALL_COMPANIES
            ? statements
            : statements.filter((s) => (s.company || "") === company);
          if (statements.length === 0) {
            return <p className="mt-3 text-sm text-slate-500 italic">Nothing uploaded yet.</p>;
          }
          if (filtered.length === 0) {
            return <p className="mt-3 text-sm text-slate-500 italic" data-testid="ops-statements-empty-filtered">No statements uploaded for {company} yet.</p>;
          }
          return (
          <div className="mt-3 rounded-2xl bg-white border border-slate-200 divide-y divide-slate-100 overflow-hidden">
            {filtered.map((s) => {
              const period = formatStatementPeriod(s);
              return (
              <div
                key={s.id}
                className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition"
                data-testid={`ops-statement-${s.id}`}
              >
                <button
                  type="button"
                  onClick={() => onOpenStatement?.(s)}
                  className="flex items-center gap-4 flex-1 min-w-0 text-left group cursor-pointer"
                  data-testid={`ops-statement-open-${s.id}`}
                  aria-label={`Review statement from ${period?.month || s.company || "statement"}`}
                >
                  <div className="h-10 w-10 rounded-lg bg-brand-mist flex items-center justify-center text-brand-navy shrink-0">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {/* Line 1: statement month (canonical) */}
                    {period?.month && (
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span
                          className="inline-flex items-center px-2.5 h-6 rounded-md bg-brand-navy text-white text-xs font-bold uppercase tracking-wider group-hover:bg-brand-teal transition-colors"
                          data-testid={`ops-statement-month-${s.id}`}
                        >
                          {period.month}
                        </span>
                      </div>
                    )}
                    {/* Line 2: period range */}
                    {period?.range && (
                      <p
                        className="mt-1 text-sm font-semibold text-brand-navy"
                        data-testid={`ops-statement-range-${s.id}`}
                      >
                        {period.range}
                      </p>
                    )}
                    {/* Line 3: company + type */}
                    <p className="mt-0.5 text-xs text-slate-500">
                      {s.company ? <span className="font-semibold text-brand-teal">{s.company}</span> : null}
                      {s.company && s.statement_type ? <span> · </span> : null}
                      {s.statement_type === "credit_card" ? <span className="text-slate-400">credit card</span> :
                       s.statement_type === "bank" ? <span className="text-slate-400">bank</span> : null}
                    </p>
                    {/* Line 4: transaction count */}
                    <p className="mt-0.5 text-xs text-slate-400">
                      {s.row_count} transactions
                    </p>
                    {/* Line 5: upload date (ISO YYYY-MM-DD) */}
                    <p className="mt-0.5 text-xs text-slate-400">
                      imported {new Date(s.created_at).toISOString().slice(0, 10)}
                    </p>
                    {/* Per-statement review status. Shows pending count
                        prominently so the operator knows exactly how many
                        rows still need classification. */}
                    {typeof s.tx_pending === "number" && (s.tx_total || 0) > 0 && (
                      <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold" data-testid={`ops-statement-pending-${s.id}`}>
                        {s.tx_pending === 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 h-5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <Check className="h-3 w-3" />
                            All {s.tx_total} reviewed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 h-5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                            {s.tx_pending} of {s.tx_total} pending
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                  <span className="text-xs font-semibold text-brand-teal shrink-0 hidden sm:inline">
                    Review →
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(s)}
                  className="h-9 w-9 rounded-md text-red-500 hover:text-white hover:bg-red-500 flex items-center justify-center transition shrink-0"
                  title="Delete statement (requires PIN)"
                  data-testid={`ops-statement-delete-${s.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              );
            })}
          </div>
          );
        })()}
      </div>

      {pendingDelete && (() => {
        const dp = formatStatementPeriod(pendingDelete);
        const label = dp?.range
          ? `${dp.month ? dp.month + " · " : ""}${dp.range}${pendingDelete.company ? " (" + pendingDelete.company + ")" : ""}`
          : (pendingDelete.company || "this statement");
        return (
          <ConfirmDeleteDialog
            title="Delete statement?"
            description={`This will permanently remove the ${label} statement AND all ${pendingDelete.row_count} transactions it produced. This cannot be undone.`}
            onClose={() => setPendingDelete(null)}
            onConfirm={doDelete}
          />
        );
      })()}
    </div>
  );
}
